import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  ResponseDto,
  OrganizationTaxRateAssignmentDto,
  UpdateOrganizationTaxRateAssignmentsDto,
} from "@mimber/schema"
import { taxRateAssignmentsKeys } from "../tax-rates.utils"

/**
 * Organization-specific hook for bulk-upserting tax rate assignments.
 * PUT /tax-rates/assignments
 * Can only be used in mimber-organization app.
 */
export const useUpdateTaxRateAssignments = (
  options?: UseMutationOptions<
    ResponseDto<OrganizationTaxRateAssignmentDto[]>,
    Error,
    UpdateOrganizationTaxRateAssignmentsDto
  >
): UseMutationResult<
  ResponseDto<OrganizationTaxRateAssignmentDto[]>,
  Error,
  UpdateOrganizationTaxRateAssignmentsDto
> => {
  guardAppUsage("mimber-organization", "useUpdateTaxRateAssignments")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (dto: UpdateOrganizationTaxRateAssignmentsDto) => {
      const response = await server.put<
        ResponseDto<OrganizationTaxRateAssignmentDto[]>
      >("tax-rates/assignments", dto)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: taxRateAssignmentsKeys.getAssignments(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
